import React, { useState, useEffect } from 'react';
import Input from '@/components/General/inputs/Input';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveCustomFieldSection } from "@/data/customFields";
import InputSection from '@/components/General/inputs/InputSection';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import { redirectToDefault } from '@/utils/constants/linking';
import ModalWrapper from '@/components/General/ModalWrapper';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg } from '@/utils/organizationEntitlements';
import PlanRestrictedScreen from '@/components/General/PlanRestrictedScreen';

const TAB_TO_MODULE = {
    "People": "People",
    "Events": "Events",
    "Small Group Types": "SmallGroupType",
};

const EMPTY_DRAFT = {};

export default function AddCustomFieldSection() {
    const { entitlements } = useOrganizationEntitlements();
    if (isFreeOrg(entitlements)) return <PlanRestrictedScreen />;
    return <AddCustomFieldSectionInner />;
}

function AddCustomFieldSectionInner() {
    const { id, type, fromScreen, fromStoreKey } = useLocalSearchParams();
    const storeKey = id || "new"
    const module = TAB_TO_MODULE[type] || "People"

    /*** State ***/
    const [ customFieldModule, setCustomFieldModule ] = useState(module);

    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData)
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData)
    const customFieldSectionDetails = useEditScreenStore(s => s.AddCustomFieldSection?.[storeKey]) || EMPTY_DRAFT

    const router = useRouter();

    /*** Queries ***/
    const sectionQuery = Query.customFieldSections.byId({id, options: {enabled: !!id}})
    const sectionToEdit = sectionQuery.data

    /*** Effects ***/
    useEffect(() => {
        if(sectionToEdit){
            setCustomFieldModule(sectionToEdit.type)
            setCustomFieldSectionDetails(sectionToEdit)
        }
    }, [sectionToEdit])

    useEffect(() => {
        if(!id && type){
            setCustomFieldModule(module)
        }
    }, [id, type])

    const setCustomFieldSectionDetails = (new_value) => {
        setEditScreenData("AddCustomFieldSection", storeKey, new_value)
    }


    /*** Queries ***/
    const queryClient = useQueryClient()

    const saveSectionMutation = useMutation({
        mutationFn: (details) => saveCustomFieldSection({ ...details, type: customFieldModule || module }),
        onSuccess: async (data) => {
            queryClient.invalidateQueries({queryKey: QueryKeys.customFieldSections.all(customFieldModule || module)})

            if(fromScreen){
                useEditScreenStore.getState().dataChangeFunction?.(data)
                router.dismissTo({pathname:fromScreen, params: fromStoreKey && fromStoreKey != "new" ? {id: fromStoreKey} : undefined});
            }
            else{
                redirectToDefault(router, "AddCustomFieldSection")
            }

            removeEditScreenData("AddCustomFieldSection", storeKey)
        }
    })


    const onSubmit = () => {
        saveSectionMutation.mutate(customFieldSectionDetails)
    }

    return (
        <ModalWrapper
            loading={saveSectionMutation.isPending}
            onCancel={() => {
                removeEditScreenData("AddCustomFieldSection", storeKey)
            }}
            onConfirm={onSubmit}
            title={id ? `Edit ${type} Section` : `New ${type} Section`}
        >
            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled">
                <InputSection title="Section Details">
                    {/* <SelectInput
                        placeholder="Module"
                        disabled={moduleDisabled}
                        backgroundColor={colors.surface}
                        options={["People", "Events"]}
                        title="Module"
                        value={customFieldModule}
                        onUpdate={(new_value) => {
                            setCustomFieldModule(new_value)
                        }}
                    /> */}

                    <Input
                        title="Section Name"
                        value={customFieldSectionDetails.name}
                        onUpdate={(new_value) => {
                            setCustomFieldSectionDetails({
                                name: new_value
                            })
                        }}
                    />
                </InputSection>
            </KeyboardAwareScrollView>

        </ModalWrapper>
    );
}
